/**
 * Appointments Hook
 *
 * Fetches patient appointments from the API.
 */

import { useQuery } from "@tanstack/react-query";
import { useApiClient } from "../../api/louise-api";
import type { paths } from "@src/packages/openapi/schema";
import {
  appointmentKeys,
  type AppointmentListKeyParams,
} from "../patient-cache-keys";
import { throwApiError } from "../../api/api-error";

export { appointmentKeys, type AppointmentListKeyParams };

// ============================================================================
// Types
// ============================================================================

type AppointmentsResponse =
  paths["/{tenantId}/patients/{patientId}/appointments"]["get"]["responses"]["200"]["content"]["application/json"];

export type Appointment = AppointmentsResponse["data"][number] & {
  startTimeLocal?: string;
  endTimeLocal?: string;
};

// ============================================================================
// Hooks
// ============================================================================

export interface UseAppointmentsOptions {
  startDate?: string;
  endDate?: string;
  date?: string;
  sort?: "asc" | "desc";
  sortBy?: "startDate" | "startTime" | "title" | "createdAt";
  enabled?: boolean;
}

/**
 * Hook to fetch appointments for the current patient.
 */
export function useAppointments(options: UseAppointmentsOptions = {}) {
  const { startDate, endDate, date, sort, sortBy, enabled = true } = options;
  const { client, tenantId, patientId } = useApiClient();

  return useQuery({
    queryKey: appointmentKeys.list({
      tenantId: tenantId ?? "",
      patientId: patientId ?? "",
      startDate,
      endDate,
      date,
      sort,
      sortBy,
    }),
    queryFn: async () => {
      const { data, error } = await client.GET(
        "/{tenantId}/patients/{patientId}/appointments",
        {
          params: {
            path: { tenantId, patientId },
            query: { startDate, endDate, date, sort, sortBy },
          },
        },
      );

      if (data) {
        return data.data as Appointment[];
      } else {
        throwApiError(error, "Failed to load appointments", "appointments");
      }
    },
    enabled: enabled && !!tenantId && !!patientId,
  });
}
